import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { useAuthStore } from "../store/auth";

type Status = "loading" | "success" | "error";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<Status>("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const { user, token, setAuth } = useAuthStore();
  const { t } = useTranslation();

  const verifyToken = searchParams.get("token");

  useEffect(() => {
    if (!verifyToken) {
      setErrorMsg(t("verify.noToken"));
      setStatus("error");
      return;
    }

    api
      .get(`/auth/verify-email?token=${verifyToken}`)
      .then(() => {
        // Patch the in-memory user so UI reflects verified status immediately
        if (user && token) {
          setAuth(token, { ...user, isEmailVerified: true });
        }
        setStatus("success");
      })
      .catch((err: unknown) => {
        const msg =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          t("verify.genericError");
        setErrorMsg(msg);
        setStatus("error");
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyToken]);

  async function handleResend() {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      setResent(true);
    } catch {
      // silent — user sees no change; they can try again
    } finally {
      setResending(false);
    }
  }

  if (status === "loading") {
    return (
      <main className="min-h-[70vh] flex items-center justify-center">
        <p className="text-gray-500 animate-pulse">{t("verify.verifying")}</p>
      </main>
    );
  }

  if (status === "success") {
    return (
      <main className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-4">✅</div>
          <h1 className="font-display text-2xl font-bold text-gray-900 mb-2">
            {t("verify.successTitle")}
          </h1>
          <p className="text-gray-500 mb-8">{t("verify.successBody")}</p>
          <Link to="/" className="btn-primary inline-block">
            {t("verify.goHome")}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="text-center max-w-sm">
        <div className="text-5xl mb-4">⚠️</div>
        <h1 className="font-display text-2xl font-bold text-gray-900 mb-2">
          {t("verify.errorTitle")}
        </h1>
        <p className="text-gray-500 mb-6">{errorMsg}</p>

        {user && !resent && (
          <button
            onClick={handleResend}
            disabled={resending}
            className="btn-primary mb-4"
          >
            {resending ? t("verify.resending") : t("verify.resend")}
          </button>
        )}

        {resent && (
          <p className="text-green-600 text-sm mb-4">{t("verify.resentOk")}</p>
        )}

        <Link to="/" className="block text-sm text-gray-400 hover:underline">
          {t("verify.goHome")}
        </Link>
      </div>
    </main>
  );
}
